import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import api from '../api/client';
import { Vehicle, Depot } from '../types';

export interface DocStatusInfo {
  status: 'vencido' | 'a_vencer' | 'em_dia' | 'sem_data';
  label: string;
  dateFormatted: string;
  badge: string;
  days: number | null;
}

export function getDocStatus(docExpiry?: string | null): DocStatusInfo {
  if (!docExpiry) {
    return {
      status: 'sem_data',
      label: 'Não informado',
      dateFormatted: '—',
      badge: 'b-gray',
      days: null
    };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  // Parse YYYY-MM-DD or ISO string safely
  const rawDateStr = docExpiry.split('T')[0];
  const parts = rawDateStr.split('-');
  let expiry: Date;
  if (parts.length === 3) {
    expiry = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  } else {
    expiry = new Date(docExpiry);
  }
  expiry.setHours(0, 0, 0, 0);

  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const dateFormatted = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : expiry.toLocaleDateString('pt-BR');

  if (diffDays < 0) {
    return {
      status: 'vencido',
      label: `Vencido há ${Math.abs(diffDays)}d`,
      dateFormatted,
      badge: 'b-danger',
      days: diffDays
    };
  } else if (diffDays <= 30) {
    return {
      status: 'a_vencer',
      label: diffDays === 0 ? 'Vence hoje' : `Vence em ${diffDays}d`,
      dateFormatted,
      badge: 'b-amber',
      days: diffDays
    };
  } else {
    return {
      status: 'em_dia',
      label: 'Em dia',
      dateFormatted,
      badge: 'b-teal',
      days: diffDays
    };
  }
}

// Mascara auto-decimal 2 casas (padrao brasileiro): digita "95" -> "0,95", "950" -> "9,50"
const maskDecimal2 = (input: string) => {
  const digits = input.replace(/\D/g, '');
  if (digits === '') return '';
  const num = parseInt(digits, 10);
  const cents = num % 100;
  const integer = Math.floor(num / 100);
  return integer.toLocaleString('pt-BR') + ',' + String(cents).padStart(2, '0');
};

const formatDecimal2BR = (num: number) => num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const unmaskDecimal = (str: string): number | null => {
  if (!str || !str.trim()) return null;
  const n = Number(str.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export default function Vehicles() {
  const { showToast } = useToast();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [depots, setDepots] = useState<Depot[]>([]);
  const [loading, setLoading] = useState(true);

  // Sorting
  const [sortCol, setSortCol] = useState<'name' | 'plate' | 'capacityKg' | 'odometerKm' | 'type' | 'active' | 'docExpiry'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Filtering states (Capacidade, CD, Documentação e Busca)
  const [selectedDepotFilter, setSelectedDepotFilter] = useState<string>('all');
  const [capacityFilter, setCapacityFilter] = useState<'all' | 'light' | 'medium' | 'heavy'>('all');
  const [docFilter, setDocFilter] = useState<'all' | 'vencido' | 'a_vencer' | 'em_dia' | 'sem_data'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modal create/edit vehicle
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVeh, setEditingVeh] = useState<Vehicle | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [type, setType] = useState('Utilitário');
  const [capacityKg, setCapacityKg] = useState(650);
  const [driverName, setDriverName] = useState('');
  const [odometerKm, setOdometerKm] = useState(15000);
  const [fuelConsumption, setFuelConsumption] = useState<number | string>(9.1);
  const [homeDepotId, setHomeDepotId] = useState('');
  const [docExpiry, setDocExpiry] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [veh, dep] = await Promise.all([
        api.get<Vehicle[]>('/vehicles'),
        api.get<Depot[]>('/depots')
      ]);
      setVehicles(veh.data);
      setDepots(dep.data);
    } catch {
      showToast('Erro ao carregar veículos.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const openCreateModal = () => {
    setEditingVeh(null);
    setName('');
    setPlate('');
    setType('Utilitário');
    setCapacityKg(650);
    setDriverName('');
    setOdometerKm(15000);
    setFuelConsumption(9.1);
    setHomeDepotId(depots[0]?.id || '');
    // Default data de vencimento: 1 ano à frente
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    setDocExpiry(nextYear.toISOString().split('T')[0]);
    setModalOpen(true);
  };

  const openEditModal = (v: Vehicle) => {
    setEditingVeh(v);
    setName(v.name);
    setPlate(v.plate);
    setType(v.type);
    setCapacityKg(v.capacityKg);
    setDriverName(v.driverName || '');
    setOdometerKm(v.odometerKm);
    let fc = '';
    if (v.fuelConsumption !== undefined && v.fuelConsumption !== null && v.fuelConsumption > 0) {
      // Registros antigos eram gravados em L/km. O formulário usa somente
      // km/L, portanto convertemos apenas esses registros legados ao abri-los.
      const kmPerLiter = v.fuelConsumption < 1 ? 1 / v.fuelConsumption : v.fuelConsumption;
      fc = formatDecimal2BR(kmPerLiter);
    }
    setFuelConsumption(fc);
    setHomeDepotId(v.homeDepotId);
    setDocExpiry(v.docExpiry ? v.docExpiry.split('T')[0] : '');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !plate.trim()) {
      showToast('Preencha o nome e a placa do veículo.', 'error');
      return;
    }
    if (!homeDepotId) {
      showToast('Selecione o depósito de origem.', 'error');
      return;
    }

    const normalizedFuelConsumption = unmaskDecimal(String(fuelConsumption ?? ''));

    if (normalizedFuelConsumption !== null && (!Number.isFinite(normalizedFuelConsumption) || normalizedFuelConsumption <= 0)) {
      showToast('Informe um consumo médio maior que zero.', 'error');
      return;
    }

    const payload = {
      name: name.trim(),
      plate: plate.trim().toUpperCase(),
      type,
      capacityKg: Number(capacityKg),
      driverName: driverName.trim() || null,
      odometerKm: Number(odometerKm),
      fuelConsumption: normalizedFuelConsumption,
      homeDepotId,
      docExpiry: docExpiry ? new Date(docExpiry).toISOString() : null
    };

    setSaving(true);
    try {
      if (editingVeh) {
        await api.patch(`/vehicles/${editingVeh.id}`, payload);
        showToast(`Veículo ${name} atualizado com sucesso!`, 'success');
      } else {
        await api.post('/vehicles', payload);
        showToast(`Veículo ${name} cadastrado com sucesso!`, 'success');
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao salvar veículo.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (v: Vehicle) => {
    try {
      await api.patch(`/vehicles/${v.id}`, { active: !v.active });
      setVehicles((prev) => prev.map((x) => (x.id === v.id ? { ...x, active: !x.active } : x)));
    } catch {
      showToast('Erro ao atualizar status do veículo.', 'error');
    }
  };

  const handleDelete = async (v: Vehicle) => {
    if (!window.confirm(`Tem certeza que deseja excluir o veículo ${v.name}?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      showToast(`Veículo ${v.name} excluído.`, 'info');
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao excluir veículo.', 'error');
    }
  };

  // Contadores de alerta de documentos
  const docAlerts = useMemo(() => {
    let expired = 0;
    let expiringSoon = 0;
    let valid = 0;
    let missing = 0;

    vehicles.forEach((v) => {
      const status = getDocStatus(v.docExpiry).status;
      if (status === 'vencido') expired++;
      else if (status === 'a_vencer') expiringSoon++;
      else if (status === 'em_dia') valid++;
      else missing++;
    });

    return { expired, expiringSoon, valid, missing };
  }, [vehicles]);

  // Filtragem e ordenação
  const filteredAndSortedVehicles = useMemo(() => {
    let result = vehicles.filter((v) => {
      // Filtro de CD
      if (selectedDepotFilter !== 'all' && v.homeDepotId !== selectedDepotFilter) {
        return false;
      }

      // Filtro de Capacidade
      if (capacityFilter === 'light' && v.capacityKg > 1000) return false;
      if (capacityFilter === 'medium' && (v.capacityKg <= 1000 || v.capacityKg > 2000)) return false;
      if (capacityFilter === 'heavy' && v.capacityKg <= 2000) return false;

      // Filtro de Documento
      if (docFilter !== 'all') {
        const dStatus = getDocStatus(v.docExpiry).status;
        if (dStatus !== docFilter) return false;
      }

      // Busca textual (Nome, Placa ou Motorista)
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = v.name.toLowerCase().includes(q);
        const matchPlate = v.plate.toLowerCase().includes(q);
        const matchDriver = (v.driverName || '').toLowerCase().includes(q);
        if (!matchName && !matchPlate && !matchDriver) return false;
      }

      return true;
    });

    // Ordenação
    return result.sort((a, b) => {
      if (sortCol === 'docExpiry') {
        const timeA = a.docExpiry ? new Date(a.docExpiry).getTime() : 0;
        const timeB = b.docExpiry ? new Date(b.docExpiry).getTime() : 0;
        return sortDir === 'asc' ? timeA - timeB : timeB - timeA;
      }
      const va: any = a[sortCol];
      const vb: any = b[sortCol];
      if (typeof va === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
  }, [vehicles, selectedDepotFilter, capacityFilter, docFilter, searchTerm, sortCol, sortDir]);

  const hasActiveFilters = selectedDepotFilter !== 'all' || capacityFilter !== 'all' || docFilter !== 'all' || searchTerm.trim() !== '';

  const clearFilters = () => {
    setSelectedDepotFilter('all');
    setCapacityFilter('all');
    setDocFilter('all');
    setSearchTerm('');
  };

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Veículos & Gestão de Frota</h1>
          <p className="page-lede">
            Frota operacional para roteirização — controle de capacidade de carga, base de origem e vencimento de documentação (CRLV).
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openCreateModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Novo veículo
          </button>
        </div>
      </div>

      {/* Banner de Alerta de Vencimento de Documentos (se houver pendências) */}
      {(docAlerts.expired > 0 || docAlerts.expiringSoon > 0) && (
        <div
          style={{
            background: docAlerts.expired > 0 ? '#fef2f2' : '#fffbeb',
            border: `1px solid ${docAlerts.expired > 0 ? '#fecaca' : '#fde68a'}`,
            borderRadius: 12,
            padding: '14px 18px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }}>{docAlerts.expired > 0 ? '🚨' : '⚠️'}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: docAlerts.expired > 0 ? '#991b1b' : '#92400e' }}>
                Atenção ao Vencimento de Documentos da Frota
              </div>
              <div style={{ fontSize: 12.5, color: docAlerts.expired > 0 ? '#b91c1c' : '#b45309', marginTop: 2 }}>
                {docAlerts.expired > 0 && <span><strong>{docAlerts.expired}</strong> veículo(s) com documento vencido. </span>}
                {docAlerts.expiringSoon > 0 && <span><strong>{docAlerts.expiringSoon}</strong> veículo(s) com documento a vencer nos próximos 30 dias.</span>}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {docAlerts.expired > 0 && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setDocFilter('vencido')}
              >
                Ver Vencidos ({docAlerts.expired})
              </button>
            )}
            {docAlerts.expiringSoon > 0 && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setDocFilter('a_vencer')}
                style={{ borderColor: '#d97706', color: '#b45309' }}
              >
                Ver a Vencer ({docAlerts.expiringSoon})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Barra de Filtros Integrados */}
      <div
        className="card"
        style={{
          padding: '16px 20px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {/* Busca Textual */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Buscar Veículo
            </label>
            <input
              type="text"
              className="input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome, placa ou motorista..."
            />
          </div>

          {/* Filtro por CD / Base */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Centro de Distribuição (CD)
            </label>
            <select
              className="input"
              value={selectedDepotFilter}
              onChange={(e) => setSelectedDepotFilter(e.target.value)}
            >
              <option value="all">🏢 Todos os CDs ({vehicles.length})</option>
              {depots.map((d) => {
                const count = vehicles.filter((v) => v.homeDepotId === d.id).length;
                return (
                  <option key={d.id} value={d.id}>
                    {d.name} ({count} veículos)
                  </option>
                );
              })}
            </select>
          </div>

          {/* Filtro por Capacidade de Carga */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Capacidade de Carga
            </label>
            <select
              className="input"
              value={capacityFilter}
              onChange={(e) => setCapacityFilter(e.target.value as any)}
            >
              <option value="all">⚖️ Todas as Capacidades</option>
              <option value="light">Até 1.000 kg (Utilitários leves)</option>
              <option value="medium">1.001 a 2.000 kg (Vans / Médios)</option>
              <option value="heavy">Acima de 2.000 kg (Caminhões)</option>
            </select>
          </div>

          {/* Filtro por Status do Documento */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Vencimento de Documento (CRLV)
            </label>
            <select
              className="input"
              value={docFilter}
              onChange={(e) => setDocFilter(e.target.value as any)}
            >
              <option value="all">📄 Todos os Documentos</option>
              <option value="vencido">🚨 Vencidos ({docAlerts.expired})</option>
              <option value="a_vencer">⚠️ A Vencer em 30d ({docAlerts.expiringSoon})</option>
              <option value="em_dia">✅ Em dia ({docAlerts.valid})</option>
              <option value="sem_data">⚪ Não informado ({docAlerts.missing})</option>
            </select>
          </div>
        </div>

        {/* Quick Sort & Filter Status Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 10,
            paddingTop: 10,
            borderTop: '1px solid var(--line-2)'
          }}
        >
          <div className="veh-sort-bar" style={{ margin: 0, padding: 0, background: 'transparent' }}>
            <span className="lbl">Ordenar por:</span>
            <button
              className={`veh-sort-chip ${sortCol === 'name' ? 'on' : ''}`}
              onClick={() => handleSort('name')}
            >
              Nome
              {sortCol === 'name' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
            </button>
            <button
              className={`veh-sort-chip ${sortCol === 'capacityKg' ? 'on' : ''}`}
              onClick={() => handleSort('capacityKg')}
            >
              Capacidade
              {sortCol === 'capacityKg' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
            </button>
            <button
              className={`veh-sort-chip ${sortCol === 'plate' ? 'on' : ''}`}
              onClick={() => handleSort('plate')}
            >
              Placa
              {sortCol === 'plate' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
            </button>
            <button
              className={`veh-sort-chip ${sortCol === 'docExpiry' ? 'on' : ''}`}
              onClick={() => handleSort('docExpiry')}
            >
              Vencimento Doc.
              {sortCol === 'docExpiry' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
            </button>
            <button
              className={`veh-sort-chip ${sortCol === 'active' ? 'on' : ''}`}
              onClick={() => handleSort('active')}
            >
              Status
              {sortCol === 'active' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Exibindo <strong>{filteredAndSortedVehicles.length}</strong> de <strong>{vehicles.length}</strong> veículos
            </span>
            {hasActiveFilters && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={clearFilters}
                style={{ color: 'var(--danger)', fontSize: 12 }}
              >
                ✕ Limpar Filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Vehicles Grid */}
      {loading ? (
        <div className="empty">
          <p>Carregando veículos...</p>
        </div>
      ) : (
        <div className="veh-grid">
          {filteredAndSortedVehicles.map((v) => {
            const depot = v.homeDepot || depots.find((d) => d.id === v.homeDepotId);
            const docInfo = getDocStatus(v.docExpiry);

            return (
              <div key={v.id} className="veh-card">
                <div className="veh-head">
                  <div className="veh-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13"></rect>
                      <polygon points="16 8 20 8 23 11 23 16 16 16 8"></polygon>
                      <circle cx="5.5" cy="18.5" r="2.5"></circle>
                      <circle cx="18.5" cy="18.5" r="2.5"></circle>
                    </svg>
                  </div>
                  <div style={{ flex: 1, marginLeft: 12 }}>
                    <div className="veh-name">{v.name}</div>
                    <div className="veh-plate">
                      {v.plate} · {v.type}
                    </div>
                  </div>
                  <span className="badge b-teal" style={{ fontSize: 10, padding: '2px 6px' }}>
                    {depot?.name.split(' ')[0] || 'CD'}
                  </span>
                </div>

                <div className="veh-body">
                  <div>
                    <div className="k">Capacidade</div>
                    <div className="v">{v.capacityKg.toLocaleString('pt-BR')} kg</div>
                  </div>
                  <div>
                    <div className="k">Motorista</div>
                    <div className="v text">{v.driverName || '—'}</div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div className="k">Hodômetro</div>
                    <div className="v mono" style={{ fontSize: 12 }}>
                      {v.odometerKm.toLocaleString('pt-BR')} km
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div className="k">CD de Origem</div>
                    <div className="v text" style={{ fontSize: 12 }}>
                      {depot?.name || '—'}
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div className="k">Consumo Médio</div>
                    <div className="v mono" style={{ fontSize: 12 }}>
                      {v.fuelConsumption ? `${v.fuelConsumption < 1 ? (1 / v.fuelConsumption).toFixed(1) : v.fuelConsumption} km/L` : 'Não informado'}
                    </div>
                  </div>

                  {/* Campo de Documento (CRLV) */}
                  <div style={{ marginTop: 8, gridColumn: 'span 2' }}>
                    <div className="k">Documento (CRLV)</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <span className={`badge ${docInfo.badge}`} style={{ fontSize: 10, padding: '2px 6px' }}>
                        <span className="dot" />
                        {docInfo.label}
                      </span>
                      {docInfo.status !== 'sem_data' && (
                        <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                          {docInfo.dateFormatted}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="veh-foot">
                  <label className="toggle" title={v.active ? 'Desativar veículo' : 'Ativar veículo'}>
                    <input
                      type="checkbox"
                      checked={v.active}
                      onChange={() => handleToggleActive(v)}
                    />
                    <span className="toggle-slider"></span>
                  </label>

                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => openEditModal(v)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(v)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredAndSortedVehicles.length === 0 && (
            <div className="empty" style={{ gridColumn: '1 / -1' }}>
              <h4>Nenhum veículo encontrado com os filtros aplicados</h4>
              <p>Tente ajustar os filtros de CD, capacidade ou termo de busca.</p>
              <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ marginTop: 10 }}>
                Limpar todos os filtros
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingVeh ? `Editar Veículo: ${editingVeh.name}` : 'Cadastrar Novo Veículo'}
        maxWidth="600px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : editingVeh ? 'Salvar alterações' : 'Cadastrar veículo'}
            </button>
          </>
        }
      >
        <div>
          <div className="field-row">
            <div className="field">
              <label>Nome / Apelido</label>
              <input
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Fiorino 07"
                autoFocus
              />
            </div>
            <div className="field">
              <label>Placa do veículo</label>
              <input
                type="text"
                className="input mono"
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="Ex: ABC-1D23"
              />
            </div>
          </div>

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Tipo de Veículo</label>
              <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="Utilitário">Utilitário (Fiorino / Kangoo)</option>
                <option value="Van">Van (Sprinter / Master)</option>
                <option value="Baú médio">Caminhão Baú Médio (HR / Bongo)</option>
                <option value="Baú grande">Caminhão Baú Grande (Delivery / Accelo)</option>
                <option value="Moto">Motocicleta (Express)</option>
              </select>
            </div>
            <div className="field">
              <label>Capacidade máxima (kg)</label>
              <input
                type="text"
                inputMode="numeric"
                className="input"
                value={capacityKg > 0 ? capacityKg.toLocaleString('pt-BR') : ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setCapacityKg(digits === '' ? 0 : parseInt(digits, 10));
                }}
                placeholder="Ex: 3.500"
              />
            </div>
          </div>

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Motorista padrão</label>
              <input
                type="text"
                className="input"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Ex: Carlos Eduardo"
              />
            </div>
            <div className="field">
              <label>CD Base de Saída</label>
              <select
                className="input"
                value={homeDepotId}
                onChange={(e) => setHomeDepotId(e.target.value)}
              >
                {depots.length === 0 && <option value="">Nenhum depósito cadastrado</option>}
                {depots.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.city})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Data de Vencimento do Documento (CRLV / Licenciamento)</label>
              <input
                type="date"
                className="input"
                value={docExpiry}
                onChange={(e) => setDocExpiry(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Hodômetro Atual (km)</label>
              <input
                type="text"
                inputMode="numeric"
                className="input mono"
                value={odometerKm > 0 ? odometerKm.toLocaleString('pt-BR') : ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setOdometerKm(digits === '' ? 0 : parseInt(digits, 10));
                }}
                placeholder="Ex: 198.760"
              />
            </div>
          </div>

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Consumo Médio (km / Litro)</label>
              <input
                type="text"
                inputMode="numeric"
                className="input mono"
                value={typeof fuelConsumption === 'number' ? formatDecimal2BR(fuelConsumption) : fuelConsumption}
                onChange={(e) => setFuelConsumption(maskDecimal2(e.target.value))}
                placeholder="Ex: 9,50 (km/L)"
              />
            </div>
            <div className="field">
              <label style={{ opacity: 0 }}>.</label>
              <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '8px 0' }}>
                💡 Ex: 9,50 km/L significa que o veículo roda 9,50 quilômetros com 1 litro de combustível.
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
